import React, { useState, useEffect } from 'react';
import {
  Text,
  VStack,
  UploadZone,
  useDisclosure,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { SmtFile } from '../../../../../../../types/Form';
import { CreateSignedDistUploadId } from '../../../../../../common/src/legacy/services/apiRouterService';
import { FieldError } from 'react-hook-form';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';
import LinkItem, { LinkEditValues } from './LinkItem';
import LinkInputUploadModal from './LinkInputUploadModal';

interface Props {
  value: SmtFile[];
  onChange: (files: SmtFile[]) => void;
  error?: FieldError;
  isDisabled?: boolean;
  withLink?: boolean;
  isEditable?: boolean;
  supportedLinkFormats?: string[];
}

export const supportedLinkFormatsFallback = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
];

const LinksInput: React.FC<React.PropsWithChildren<Props>> = ({
  value = [],
  onChange,
  error,
  isDisabled,
  isEditable,
  supportedLinkFormats = supportedLinkFormatsFallback,
  withLink,
}) => {
  const { distribution } = useAuth();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [concattedCount, setConcattedCount] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(error?.message);

  useEffect(() => {
    setErrorMsg(error?.message);
  }, [error]);

  const uploadFile = async (file: File) => {
    const [resp, status] = await CreateSignedDistUploadId(distribution?.id, {
      filename: file.name,
    });
    if (status !== 200) {
      try {
        await fetch(resp.link, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/pdf' },
          body: new Blob([file], { type: file.type }),
        });

        return {
          document_id: resp.document_id,
          location: resp.location,
          filename: file.name,
          size: file.size,
          filetype: file.type,
          description: undefined,
          public_bucket_link: resp.public_bucket_link,
          public_url: resp.public_url,
        };
      } catch (err) {
        return Promise.reject();
      }
    } else {
      return Promise.reject();
    }
  };

  const handleUploads = async (files: File[]) => {
    try {
      setErrorMsg(undefined);
      setFiles((prevFiles) => [...prevFiles, ...files]);

      const newFiles = await Promise.all(files.map((file) => uploadFile(file)));
      onChange([...value, ...newFiles]);
    } catch {
      setErrorMsg(t('Upload of a document has failed'));
    }
  };

  const onRemove = (index: number) => {
    const updatedFiles = files.filter((_, _index) => _index !== index);
    setFiles(updatedFiles);
    setErrorMsg(undefined);

    // Update answers state
    onChange(value.filter((_, _index) => _index !== index));
  };

  const fetchAllFiles = async (uploadedFiles: SmtFile[]) => {
    const files = await Promise.all(
      uploadedFiles.map(async (file) => {
        if (file.public_bucket_link) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response = await fetch(file.public_bucket_link as any);
          const responseBlob = await response.blob();
          return new File([responseBlob], file.filename, {
            type: response.headers.get('Content-Type') || '',
          });
        }

        return Promise.reject();
      }),
    );

    setFiles(files);
  };

  useEffect(() => {
    if (value?.length > 0) fetchAllFiles(value);
  }, [value]);

  const handleEditFile = (formValues: LinkEditValues, index: number) => {
    const newAnswer = [...value];
    newAnswer[index] = { ...newAnswer[index], ...formValues };
    onChange(newAnswer);
  };

  return (
    <>
      {isOpenModal && (
        <LinkInputUploadModal
          value={value.slice(concattedCount)}
          files={files.slice(concattedCount)}
          supportedLinkFormats={supportedLinkFormats}
          onClose={onCloseModal}
          handleUploads={handleUploads}
          onRemove={(i) => onRemove(i + concattedCount)}
          onChange={(values) =>
            onChange([...value.slice(0, concattedCount), ...values])
          }
          isEditable={isEditable}
        />
      )}
      <VStack align="stretch" spacing="2">
        <UploadZone
          onFilesAccepted={(newFiles: File[]) => {
            setConcattedCount(files.length);
            handleUploads(newFiles);
            onOpenModal();
          }}
          accept={supportedLinkFormats}
          multiple
          isDisabled={isDisabled}
        />
        {files.map((file, i) => (
          <LinkItem
            key={
              i +
              Boolean(value[i]?.filename).toString() +
              Boolean(value[i]?.description).toString()
            }
            file={file}
            document={value[i]}
            onRemove={() => onRemove(i)}
            onSubmit={(values) => handleEditFile(values, i)}
            isEditable={isEditable}
          />
        ))}
        {errorMsg && <Text color="danger.main">{errorMsg}</Text>}
      </VStack>
    </>
  );
};

export default LinksInput;
